/**
 * ReadModeAnnotationOverlay — "Anmerken" UI for the read-mode view.
 *
 * Unlike AnnotationOverlay (which speaks ProseMirror selection coords),
 * this overlay listens to the browser's native window.getSelection() so
 * the user can highlight any rendered text and annotate it without
 * switching into edit-mode.
 *
 * Block resolution (2026-06-12): we don't have a `data-block-id` per
 * paragraph in the markdown-rendered HTML (markdown→HTML loses paragraph
 * metadata), so we resolve the selection back to the structured
 * paragraph list by **substring search** on the captured text. The
 * first paragraph that contains the snippet wins; offsets inside the
 * paragraph are derived from indexOf. False positives are possible only
 * when the same exact snippet appears verbatim in multiple paragraphs —
 * rare in practice, and the snippet itself is stored as anchor_quote so
 * AnnotationHighlight's fallback still finds the right span.
 *
 * Falls back to an "Edit-Mode öffnen" hint when:
 *   - selection spans multiple paragraphs
 *   - selection text can't be located in any paragraph (e.g. markdown
 *     formatting eats it — bold/italic boundaries)
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { CONTENT_API } from '../config'
import { getAccessToken, getUser } from '../auth'

interface StructuredParagraph {
  index: number
  text: string
  block_id?: string | null
}

interface Props {
  postId: number
  /** Live structured paragraph list from CollabReadView's Yjs mirror. */
  paragraphs: StructuredParagraph[] | null
  /** Root DOM element we listen on for selection changes. */
  readModeRoot: HTMLElement | null
  /** Called after a successful annotation create — parent may want to
   *  do nothing (Yjs WS pushes the new section in automatically) but
   *  the hook is here for symmetry with AnnotationOverlay. */
  onCreated?: () => void
}

interface ButtonPosition {
  top: number
  left: number
}

interface ResolvedAnchor {
  block_id: string
  anchor_start: number
  anchor_end: number
  snippet: string
}

function resolveAnchor(
  paragraphs: StructuredParagraph[],
  snippet: string,
): ResolvedAnchor | null {
  const needle = snippet.trim()
  if (!needle) return null
  for (const p of paragraphs) {
    if (!p.block_id) continue
    const txt = p.text || ''
    const idx = txt.indexOf(needle)
    if (idx >= 0) {
      return {
        block_id: p.block_id,
        anchor_start: idx,
        anchor_end: idx + needle.length,
        snippet: needle,
      }
    }
  }
  return null
}

export function ReadModeAnnotationOverlay({
  postId,
  paragraphs,
  readModeRoot,
  onCreated,
}: Props) {
  const [buttonPos, setButtonPos] = useState<ButtonPosition | null>(null)
  const [anchor, setAnchor] = useState<ResolvedAnchor | null>(null)
  const [showDialog, setShowDialog] = useState(false)
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)

  // Listen for selection changes inside the read-mode root.
  useEffect(() => {
    if (!readModeRoot) return
    const handler = () => {
      if (showDialog) return
      const sel = window.getSelection()
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
        setButtonPos(null)
        return
      }
      const range = sel.getRangeAt(0)
      // Selection must be entirely inside the read-mode root.
      if (
        !readModeRoot.contains(range.startContainer) ||
        !readModeRoot.contains(range.endContainer)
      ) {
        setButtonPos(null)
        return
      }
      const text = sel.toString().trim()
      if (text.length < 2) {
        setButtonPos(null)
        return
      }
      const resolved = paragraphs ? resolveAnchor(paragraphs, text) : null
      if (!resolved) {
        // Can't anchor — multi-paragraph or markdown ate the snippet.
        setButtonPos(null)
        setAnchor(null)
        return
      }
      const rect = range.getBoundingClientRect()
      setAnchor(resolved)
      setButtonPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      })
    }
    // selectionchange is on document. Filter by root membership inside.
    document.addEventListener('selectionchange', handler)
    return () => {
      document.removeEventListener('selectionchange', handler)
    }
  }, [readModeRoot, paragraphs, showDialog])

  const openDialog = useCallback(() => {
    setShowDialog(true)
    setButtonPos(null)
  }, [])

  const closeDialog = useCallback(() => {
    setShowDialog(false)
    setNote('')
    setAnchor(null)
    // Clear the selection so the button doesn't pop back on next move.
    try {
      window.getSelection()?.removeAllRanges()
    } catch {
      /* ignore */
    }
  }, [])

  const submit = useCallback(async () => {
    if (!anchor) return
    if (!note.trim()) return
    const token = getAccessToken()
    if (!token) return
    const user = getUser()
    const initiator = user
      ? `${user.first_name} ${user.last_name}`.trim() || user.email
      : 'Anon'
    setSubmitting(true)
    try {
      const r = await fetch(
        `${CONTENT_API}/api/v1/posts/${postId}/sections/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            type: 'annotation',
            attrs: {
              initiator,
              anchor_paragraph_id: anchor.block_id,
              anchor_start: anchor.anchor_start,
              anchor_end: anchor.anchor_end,
              anchor_quote: anchor.snippet,
            },
            initial_text: `<main>${note.trim()}</main>`,
          }),
        },
      )
      if (r.ok) {
        closeDialog()
        onCreated?.()
      } else {
        const errText = await r.text()
        alert(`Fehler beim Speichern: ${r.status} ${errText}`)
      }
    } catch (e) {
      alert(`Netzwerk-Fehler: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setSubmitting(false)
    }
  }, [anchor, note, postId, closeDialog, onCreated])

  // Close on Escape; ⌘+Enter to submit.
  useEffect(() => {
    if (!showDialog) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDialog()
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [showDialog, closeDialog, submit])

  return (
    <>
      {buttonPos && !showDialog && (
        <button
          type="button"
          onMouseDown={(e) => {
            // Don't let the mousedown collapse our selection before the
            // click handler fires.
            e.preventDefault()
          }}
          onClick={openDialog}
          style={{
            position: 'absolute',
            top: buttonPos.top,
            left: buttonPos.left,
            zIndex: 100,
            padding: '4px 10px',
            background: '#222',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            fontSize: 12,
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
          }}
        >
          💬 Anmerken
        </button>
      )}

      {showDialog && anchor && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 200,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeDialog()
          }}
        >
          <div
            ref={dialogRef}
            style={{
              background: '#fff',
              padding: 20,
              borderRadius: 8,
              maxWidth: 500,
              width: '90vw',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
          >
            <h3 style={{ margin: '0 0 12px 0', fontSize: 16 }}>
              Anmerkung hinzufügen
            </h3>
            <blockquote
              style={{
                margin: '0 0 12px 0',
                padding: '8px 12px',
                background: '#f5f5f5',
                borderLeft: '3px solid #888',
                fontStyle: 'italic',
                fontSize: 14,
                maxHeight: 120,
                overflowY: 'auto',
              }}
            >
              {anchor.snippet}
            </blockquote>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Deine Anmerkung dazu …"
              autoFocus
              style={{
                width: '100%',
                minHeight: 100,
                padding: 8,
                border: '1px solid #ccc',
                borderRadius: 4,
                fontFamily: 'inherit',
                fontSize: 14,
                boxSizing: 'border-box',
                resize: 'vertical',
              }}
            />
            <div
              style={{
                marginTop: 12,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={closeDialog}
                style={{
                  padding: '6px 14px',
                  background: 'transparent',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting || !note.trim()}
                style={{
                  padding: '6px 14px',
                  background: submitting ? '#999' : '#222',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: submitting ? 'wait' : 'pointer',
                }}
              >
                {submitting ? 'Speichert …' : 'Anmerkung senden (⌘+Enter)'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
